export class DriverViewModel {
  id!: string;
  driverNo!: string;
  name!: string;
  middleName!: string;
  lastName!: string;
  /** Preferred first name (defaults to the first name). Shown on the dispatch pages and driver notes. */
  preferredName!: string;
  email!: string;
  altEmail!: string;
  cellphone!: string;
  phone!: string;
  birthDate!: Date;
  hiredDate!: Date;
  hiredDateTo!: Date;
  licenseNumber!: string;
  licenseState!: string;
  licenseExpirationDate!: Date;
  homeAddress!: string;
  city!: string;
  state!: string;
  zipCode!: string;
  emergencyContName!: string;
  emergencyContPhone!: string;
  taxId!: string;
  manager!: string;
  operatorId!: string;
  driverId!: string;
  lessorId!: string;
  type!: string;
  avatarDriverType!: string;
  terminationType!: string;
  socialSecurityNumber!: string;
  physicalExamDate!: Date;
  physicalExamExpirationDate!: Date;
  annualReviewDate!: Date;
  trainingDateCGMP!: Date;
  expirationDateCGMP!: Date;
  hazmatTrainingDate!: Date;
  hazmatTrainingExpirationDate!: Date;
  hmeExpirationDate!: Date;
  passportExpirationDate!: Date;
  bonusPerMile!: number;
  peopleNetId!: string;
  terminated!: boolean;
  terminationDate!: Date;
  details!: string;
  isActive!: boolean;
  rehire!: boolean;
  tchekCardNo?: string;
  isPayTo!: boolean;
  isPtoEligible?: boolean | null;
  isPtoEligibleDefault?: boolean | null;
  earningPercentage!: number;
  settlementEmail!: string;
  picture!: string;
  efsDriverId?: string;
  isOffToday?: boolean;

  driverHireDates!: any[];
  team!: any;
  teamId?: string;
  tractors!: any[];

  // compliance
  doNotDispatch?: boolean;
  outOfCompliance?: boolean;
  productQualifications?: DriverProductQualification[];
  customerTraining?: DriverCustomerTraining[];
  inDepthSecurityExpirationDate?: Date;
  twicCardExpirationDate?: Date;
  autoTransmissionOnly?: boolean;
  lastAvatarSyncDate?: Date;

  // notes
  notes?: DriverNote[];

  // assignments
  tractorId?: string;
  /** Marks the tractor assignment as a spare unit (shown on the dispatch driver assignments grid). */
  tractorIsSpare?: boolean;
  trailerId?: string;
  clientId?: string;
  billToId?: string;
  specialNote?: string;

  // scheduling team assignments
  schedulingTeamName?: string;
  schedulingTeamClientId?: string; /** Contact emails of the driver's scheduling team - default recipients for emails to the customer. */
  schedulingTeamContactEmails?: string[];
  /** Names of the other drivers on the driver's team (the co-driver), shown alongside the driver in note emails. */
  teamMembers?: string[];
  billToClientName?: string;
  billToClientId?: string;
  boardName?: string;

  preferredClients?: DriverPreferredClient[];
  bannedClients?: DriverBannedClient[];
  preferredAreas?: DriverPreferredArea[];
  assignmentHistory?: DriverAssignmentHistory[];

  // client only
  previousTeam!: any;
}

export interface DriverPreferredClient {
  id?: string;
  driverId?: string;
  clientId: string;
  clientName?: string;
  customerNo?: string;
}

export interface DriverBannedClient {
  id?: string;
  driverId?: string;
  clientId: string;
  clientName?: string;
  customerNo?: string;
}

export interface DriverPreferredArea {
  id?: string;
  driverId?: string;
  gasRegionId: string;
  regionName?: string;
}

export interface DriverAssignmentHistory {
  id?: string;
  driverId?: string;
  date: Date;
  type: string;
  assignedTo: string;
  user: string;
}

export interface DriverProductQualification {
  id?: string;
  driverId?: string;
  productQualificationId: string;
  productQualificationName?: string;
  productQualificationAvatarFieldName?: string;
  qualification?: string;
}

export interface DriverCustomerTraining {
  id?: string;
  driverId?: string;
  trainingName: string;
  qualification?: string;
}

export interface DriverNote {
  id?: string;
  driverId?: string;
  driverName?: string;
  driverNo?: string;
  note?: string;
  status?: string;
  type?: string;
  dateCreatedUtc?: Date;
  createdByUserId?: string;
  createdByUserName?: string;

  /** Internal user tagged on a "Complaint" note. */
  taggedUserId?: string;
  taggedUserName?: string;

  // Accident/Incident fields
  anyInjuries?: boolean;

  /** Which of the "Any Injuries, Tow aways, or Fatalities?" answers apply when that answer is Yes. */
  hasInjuries?: boolean;
  hasTowAway?: boolean;
  hasFatality?: boolean;

  anotherVehicleInvolved?: boolean;
  driverCitation?: boolean;
  citationDescription?: string;
  injuriesDescription?: string;
  anotherVehicleDescription?: string;
  tractorId?: string;
  tractorNumber?: string;
  trailerId?: string;
  trailerNumber?: string;
  shipperTrailerInformation?: string;
  loadedEmpty?: string;
  productId?: string;
  productName?: string;
  accidentDate?: string;
  accidentTime?: string;
  timeZone?: string;
  accidentCity?: string;
  accidentState?: string;
  accidentLocation?: string;
  weather?: string;
  accidentDescription?: string;
  damageDescription?: string;
  assignedCustomer?: string;
  photosToSafety?: string;
  spokenWithThirdParty?: boolean;
  thirdPartyContactName?: string;

  // Delay/Breakdown fields
  clientId?: string;
  clientName?: string;
  billToId?: string;
  billToName?: string;
  tripNumber?: string;
  customerReferenceNumber?: string;
  eta?: Date;
  truckTrailerIssue?: string;
  hosRemaining?: number;
  internalNotes?: string;
  clientNotes?: string;
  roadCallNumber?: string;
  startDate?: Date;
  endDate?: Date;

  // Driver Release fields
  driverLocation?: string;
  requestTimeOff?: boolean;
  trailerDropoffLocation?: string;
  nextAvailable?: string;

  // Common optional field
  followUpDate?: Date;

  /**
   * Reference attachments on a General note (PDFs and images). Stored with the note for future
   * reference only - they are never included in the note's emails.
   */
  attachments?: DriverNoteAttachment[];

  // Email tracking fields
  emailedToSafety?: Date;
  emailedFormToDriver?: Date;
  emailedToClient?: Date;
  emailedToDriver?: Date;

  // Status updates (breakdown follow-ups)
  updates?: DriverNoteUpdate[];
}

/**
 * A reference file kept with a driver note. Never included in the note's emails; the client builds
 * the inline preview URL from the blob name.
 */
export interface DriverNoteAttachment {
  id?: string;
  driverNoteId?: string;
  /** Blob name in the note attachments container. */
  name?: string;
  originalName?: string;
  type?: string;
  dateCreatedUtc?: Date;
}

export interface DriverNoteUpdate {
  id?: string;
  driverNoteId?: string;
  label?: string;
  internalNotes?: string;
  clientNotes?: string;
  createdByUserId?: string;
  createdByUserName?: string;
  dateCreatedUtc?: Date;
  emailedToClient?: Date;
  emailedToDriver?: Date;
}

/** A record of an email that was sent from a driver note (used to rebuild threads). */
export interface DriverNoteEmail {
  id?: string;
  driverNoteId?: string;
  driverNoteUpdateId?: string;
  emailType?: string;
  toEmails?: string;
  ccEmails?: string;
  subject?: string;
  body?: string;
  sentByUserId?: string;
  sentByUserName?: string;
  dateCreatedUtc?: Date;
}
